import { useEffect, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';

import moment from 'moment';
import { Link } from 'react-router-dom';
import {
  getTrailerRequestByUserIDEffect,
  getTrailerRequestsEffect,
  getUserRelationEffect,
} from '../../../redux/Savers/SaversEffects';
import '../../../styles/savers/custom.css';
import { setTrailerRequestId } from '@/redux/Savers/SaversActions';
import { exportDataToExcel } from '@/utilz';
import TrailerScsSelector from '@/views/components/Selectors/TrailerScsSelector';
import TrailerStatusSelector from '@/views/components/Selectors/TrailerStatusSelector';
import TrailerStoreIdSelector from '@/views/components/Selectors/TrailerStoreIdSelector';
import { ArrowRightOutlined } from '@ant-design/icons';
import { Col, DatePicker, Form, Row, message } from 'antd';
import { uniqBy } from 'es-toolkit';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import SecondaryLabel from '../../components/Labels/SecondaryLabel';
import ContentSpinner from '../../components/Spinners/ContentSpinner';
import SaversHomeGrid from './SaversHomeGrid';

const dateFormatter = (cell) => {
  const newDate = cell && moment(cell).format('MMM D YYYY h:mm A');
  return <span>{newDate}</span>;
};

const columnFormatter = () => {
  return (
    <span>
      {' '}
      <Link className="color-1" to="">
        <ArrowRightOutlined />
      </Link>{' '}
    </span>
  );
};

export default function Home() {
  const [formHome] = Form.useForm();
  const { RangePicker } = DatePicker;

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const companyId = useSelector((state) => state.auth.selectedCompany.companyId, shallowEqual);
  const userType = useSelector((state) => state.savers.loggedInUser.data);

  const [selectedRange, setSelectedRange] = useState([]);
  const [trailerRequests, setTrailerRequests] = useState([]);
  const [filters, setFilters] = useState({
    filterValue: '',
    rscmFilterValue: '',
    cscFilterValue: '',
    assignedToFilterValue: '',
    storeIdFilterValue: '',
  });
  const [trailerRequestCt, setTrailerRequestCt] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errMessage, setErrMessage] = useState('');
  const [userData, setUserData] = useState({
    trlHidden: false,
    rscm: '',
    scs: '',
    branchDesc: '',
  });
  const [relationId, setRelationId] = useState('');
  const [pageIndex, setPageIndex] = useState(1);
  const [currentPage, setCurrentPage] = useState(0);
  const [loadingNextPage, setLoadingNextPage] = useState(false);
  const [vendorId, setVendorId] = useState('');
  const { filterValue, rscmFilterValue, cscFilterValue, assignedToFilterValue, storeIdFilterValue } = filters;
  const { trlHidden } = userData;

  const mergeUniqueRequests = (existingData, newData) => {
    return uniqBy([...existingData, ...newData], (item) => item.trailerRequestID);
  };

  const getTrailerRequestsByUser = async ({ userIDValue, pageNo }) => {
    try {
      const { data } = await getTrailerRequestByUserIDEffect(userIDValue, pageNo);

      setCurrentPage(data.totalRequestCount);
      if (data.trailerRequestData.length === 0) {
        setErrMessage('Data not found');
      } else {
        setErrMessage('');
        setTrailerRequests((prev) => mergeUniqueRequests(prev, data.trailerRequestData));
      }
    } catch (error) {
      console.error(error);
      setTrailerRequests([]);
    }
  };

  const getTrailerRequests = async () => {
    try {
      const { data } = await getTrailerRequestsEffect({ pageNo: 1 });
      setCurrentPage(data.totalRequestCount);
      if (data.trailerRequestData.length === 0) {
        setErrMessage('Data not found');
      } else {
        setErrMessage('');
        setTrailerRequests((prev) => mergeUniqueRequests(prev, data.trailerRequestData));
      }
    } catch (error) {
      console.error(error);
      setTrailerRequests([]);
    }
  };

  const LoadTrailerRequestData = (requestID) => {
    dispatch(setTrailerRequestId(requestID));
    navigate('/Trailer-Review');
  };

  const getUserRelations = async () => {
    try {
      const { data } = await getUserRelationEffect();

      if (data.length > 0) {
        const user = data[0];
        setRelationId(user.userID);
        setVendorId(user.branchCustRef);
        if (user.userType === 'Store' || user.userType === 'Supplier') {
          setUserData({
            trlHidden: true,
            rscm: user.rscm,
            scs: user.scs,
            branchDesc: user.branchDesc,
          });
          formHome.setFieldsValue({
            Supplier: user.branchDesc,
            RSCM: user.rscm,
            SCS: user.scs,
          });
          await getTrailerRequestsByUser({ userIDValue: user.userID, pageNo: 1 });
        } else if (user.userType === 'CT') {
          setUserData({ trlHidden: false, rscm: '', scs: '', branchDesc: '' });
          getTrailerRequests();
        }
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleNextPage = async () => {
    if (loadingNextPage || currentPage === trailerRequestCt.length) {
      return;
    }

    try {
      setLoadingNextPage(true);
      const nextPageNo = pageIndex + 1;
      setPageIndex(pageIndex + 1);
      if (userType === 'CT') {
        const { data } = await getTrailerRequestsEffect({
          pageNo: nextPageNo,
        });
        const filteredTrailerRequests = filterData(data.trailerRequestData);
        setTrailerRequestCt((prev) => [...prev, ...filteredTrailerRequests]);
      } else if (userType === 'Store  ' || userType === 'Supplier') {
        const { data } = await getTrailerRequestsByUser({ userIDValue: relationId, pageNo: nextPageNo });
        const filteredTrailerRequests = filterData(data);
        setTrailerRequestCt((prev) => [
          ...prev,
          ...filteredTrailerRequests.filter((row) => {
            const createdDateMoment = moment(row.createdOn).format('YYYY-MM-DD');
            const sevenDaysAgo = moment().subtract(7, 'days').startOf('day');
            const createdInLast7Days = moment(createdDateMoment).isAfter(sevenDaysAgo);
            return createdInLast7Days;
          }),
        ]);
      }
    } catch (error) {
      console.error('Error loading next page:', error);
    } finally {
      setLoadingNextPage(false);
    }
  };

  const columns = [
    {
      dataField: 'trailerRequestID',
      text: 'id',
      hidden: true,
    },
    {
      dataField: 'trailerNumber',
      text: 'Trailer #',
      headerStyle: { textAlign: 'center' },
      sort: true,
      bgColor: '#f2f2f2',
      align: 'left',
    },
    {
      dataField: 'branchCustRef',
      text: 'Location ID',
      headerStyle: { textAlign: 'center' },
      sort: true,
      align: 'right',
    },
    {
      dataField: 'fullName',
      text: 'Assigned To',
      headerStyle: { textAlign: 'center' },
      sort: true,
      align: 'left',
    },
    {
      dataField: 'rscm',
      text: 'RSCM',
      headerStyle: { width: '150px', textAlign: 'center' },
      align: 'left',
    },
    {
      dataField: 'scs',
      text: 'SCS',
      headerStyle: { width: '150px', textAlign: 'center' },

      align: 'left',
    },
    {
      dataField: 'deliveryProductTypeDesc',
      text: 'Delivered Product Type',
      headerStyle: { width: '150px', textAlign: 'center' },
      align: 'left',
    },
    {
      dataField: 'deliveryDate',
      text: 'Delivery Date',
      headerStyle: { width: '150px', textAlign: 'center' },
      sort: true,
      formatter: dateFormatter,
      align: 'left',
    },
    {
      dataField: 'trailerRequestStatusDesc',
      text: 'Status',
      headerStyle: { textAlign: 'center' },
      align: 'left',
      sort: true,
    },
    {
      dataField: 'specialInstruction',
      text: 'Comments',
      headerStyle: { textAlign: 'center' },

      align: 'left',
    },
    {
      dataField: 'trailerRequestID',
      text: 'Go to Request',
      headerStyle: { textAlign: 'center' },

      align: 'center',
      formatter: columnFormatter,
      events: {
        onClick: (_e, _column, _columnIndex, row, _rowIndex) => {
          LoadTrailerRequestData(row.trailerRequestID);
        },
      },
      hidden: trlHidden,
    },
  ];

  const compareDate = (dateToCompare) => {
    if (selectedRange.length !== 2) {
      message.warning('Please select a date range first!');
      return;
    }

    const startDate = selectedRange[0];
    const endDate = selectedRange[1];

    if (startDate && endDate) {
      const isWithinRange = dateToCompare >= startDate && dateToCompare <= endDate;

      return isWithinRange;
    }
  };

  const filterData = (data) => {
    if (
      !filterValue &&
      !rscmFilterValue &&
      !cscFilterValue &&
      !assignedToFilterValue &&
      !storeIdFilterValue &&
      selectedRange.length === 0
    ) {
      return data;
    }

    return data.filter((row) => {
      const filters = [
        !filterValue || String(filterValue).toLowerCase().includes(String(row.trailerRequestStautsID).toLowerCase()),
        !rscmFilterValue || String(row.rscm).toLowerCase() === String(rscmFilterValue).toLowerCase(),
        !cscFilterValue || String(row.scs).toLowerCase() === String(cscFilterValue).toLowerCase(),
        !assignedToFilterValue || String(row.fullName).toLowerCase() === String(assignedToFilterValue).toLowerCase(),
        selectedRange.length === 0 || compareDate(moment(row.createdOn).format('YYYY-MM-DD')),
        !storeIdFilterValue || String(row.locationID).toLowerCase() === String(storeIdFilterValue).toLowerCase(),
      ];

      return filters.every(Boolean);
    });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        await getUserRelations();
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [companyId]);

  useEffect(() => {
    const filteredTrailerRequests = filterData(trailerRequests);
    if (userType.toLowerCase() !== 'ct' && selectedRange.length === 0) {
      setTrailerRequestCt(
        filteredTrailerRequests.filter((row) => {
          const createdDateMoment = moment(row.createdOn).format('YYYY-MM-DD');
          const sevenDaysAgo = moment().subtract(7, 'days').startOf('day');
          const createdInLast7Days = moment(createdDateMoment).isAfter(sevenDaysAgo);
          return createdInLast7Days;
        }),
      );
    } else {
      setTrailerRequestCt(filteredTrailerRequests);
    }
  }, [selectedRange, userType, trailerRequests, filters]);

  const handleExport = () => {
    const filteredColumns = columns.filter((column) => !column.hidden && column.dataField !== 'trailerRequestID');
    exportDataToExcel(trailerRequestCt, 'Trailer_Request_Data', filteredColumns);
  };

  const handleChange = (_dates, dateStrings) => {
    const cleanData = dateStrings.filter((item) => item?.trim());
    if (cleanData.length > 0) {
      setSelectedRange(cleanData);
    } else {
      setSelectedRange([]);
    }
  };

  const handleFilterChange = (value, key) => {
    if (key === 'filterValue') {
      setFilters((prev) => ({ ...prev, filterValue: value.join(',') }));
    } else if (key === 'storeIdFilterValue') {
      setFilters((prev) => ({ ...prev, [key]: value?.value || '' }));
    } else {
      setFilters((prev) => ({ ...prev, [key]: value?.label || '' }));
    }
  };

  return (
    <>
      <Row gutter={20}>
        <Col sm={24}>
          <span className="pull-right color-1" style={{ position: 'fixed', right: '32px', marginTop: '-26px' }}>
            <strong> {userType} </strong> Logged In{' '}
          </span>
        </Col>
      </Row>
      <div className="paper-container h-100 w-100">
        <Form form={formHome}>
          <div className="py-5 px-10">
            {userType !== 'Supplier' && (
              <Row gutter={10}>
                <Col sm={6}>
                  <SecondaryLabel className="f-12 text-black"> Filter by Status </SecondaryLabel>
                  <TrailerStatusSelector
                    id="selStatus"
                    mode="multiple"
                    className="mt-2 w-100"
                    placeholder="Filter by Status"
                    onChange={(value) => handleFilterChange(value, 'filterValue')}
                  />
                </Col>
                <Col sm={4}>
                  <TrailerStoreIdSelector
                    label="Filter by Supplier/Store ID"
                    editMode
                    id="selSupplier"
                    name="Supplier"
                    margin="m-0"
                    placeholder="Filter by Supplier/Store ID"
                    onChange={(value) => handleFilterChange(value, 'storeIdFilterValue')}
                    disabled={userType !== 'CT'}
                    isIDSelector={false}
                  />
                </Col>
                <Col sm={3}>
                  <TrailerScsSelector
                    label="Filter by RSCM"
                    editMode
                    id="selRSCM"
                    name="RSCM"
                    margin="m-0"
                    customerKey="RSCM"
                    placeholder="Filter by RSCM"
                    onChange={(value) => handleFilterChange(value, 'rscmFilterValue')}
                    disabled={userType !== 'CT'}
                  />
                </Col>
                <Col sm={3}>
                  <TrailerScsSelector
                    label="Filter by SCS"
                    editMode
                    id="selSCS"
                    name="SCS"
                    margin="m-0"
                    placeholder="Filter by SCS"
                    customerKey="SCS"
                    onChange={(value) => handleFilterChange(value, 'cscFilterValue')}
                    disabled={userType !== 'CT'}
                  />
                </Col>
                <Col sm={3}>
                  <TrailerScsSelector
                    label="Filter by Assigned to"
                    editMode
                    id="selAssignedTo"
                    name="AssignedTo"
                    margin="m-0"
                    customerKey="CT"
                    placeholder="Filter by Assigned To"
                    onChange={(value) => handleFilterChange(value, 'assignedToFilterValue')}
                  />
                </Col>
                <Col sm={5}>
                  <SecondaryLabel className="f-12 text-black"> Filter by Request Creation Date </SecondaryLabel>
                  <RangePicker
                    id="dtFilterDate"
                    name="filterByDate"
                    onChange={handleChange}
                    className="mt-2 w-100"
                    placeholder={['Start Date', 'End Date']}
                  />
                </Col>
              </Row>
            )}
            <Row gutter={20} justify="space-between">
              <Col className="my-4">
                <div>
                  <button
                    className="btn btn-dipblue"
                    onClick={() => navigate('/Trailer-Request', { state: { vendorId } })}
                    type="button"
                  >
                    Request Trailer{' '}
                  </button>
                </div>
              </Col>

              <Col className="my-4 mx-1 text-end">
                <button className="btn btn-dipblue" type="button" onClick={handleExport}>
                  Export to Excel
                </button>
              </Col>
            </Row>
            {loading ? (
              <div style={{ marginTop: '15%' }}>
                <ContentSpinner size="large" />
              </div>
            ) : (
              <SaversHomeGrid
                handleNextPage={handleNextPage}
                trailerRequestCt={trailerRequestCt}
                errMessage={errMessage}
                columns={columns}
              />
            )}
          </div>
        </Form>
      </div>
    </>
  );
}
